export type IdentifierValidationKind =
    | 'cid'
    | 'domain'
    | 'email'
    | 'formsUrl'
    | 'gamertag'
    | 'guid'
    | 'ip'
    | 'mrn'
    | 'ncmecReportId'
    | 'phone'
    | 'puid'
    | 'sharePointUrl'
    | 'skypeId'
    | 'url'
    | 'xbox5x5Token';

export interface IdentifierTypeOption {
    value: string;
    label: string;
    placeholder: string;
    example?: string;
    requiresDateRange?: boolean;
    validation?: IdentifierValidationKind;
    maxLength?: number;
}

// Identifiers are filed as inline CMS DFTs, which CMS caps at 50 per case; the manual
// service list feeds CMS's Services array, capped the same way. Both limits mirror the
// server-side SubmitDomesticCaseRequestValidator so the wizard never lets an agent build a
// request the API — and CMS behind it — would reject.
export const MAX_IDENTIFIERS = 50;
export const IDENTIFIER_PREVIEW_LIMIT = 5;

export const IDENTIFIER_TYPES: IdentifierTypeOption[] = [
    { value: 'CID', label: 'CID', placeholder: 'Enter CIDs', validation: 'cid' },
    {
        value: 'Domain Name',
        label: 'Domain Name',
        placeholder: 'Enter domains',
        example: 'example.com',
        validation: 'domain',
    },
    {
        value: 'Email Address',
        label: 'Email Address',
        placeholder: 'Enter email addresses',
        example: 'user1@domain.com, user2@domain.com',
        validation: 'email',
    },
    {
        value: 'IP Address',
        label: 'IP Address',
        placeholder: 'Enter IP addresses',
        example: '192.168.1.1, 10.0.0.5',
        validation: 'ip',
    },
    {
        value: 'Microsoft Forms URL',
        label: 'Microsoft Forms URL',
        placeholder: 'Enter Microsoft Forms URLs',
        example: 'https://forms.office.com/...',
        validation: 'formsUrl',
        maxLength: 2048,
    },
    { value: 'MRN', label: 'MRN', placeholder: 'Enter MRNs', validation: 'mrn' },
    { value: 'NCMEC Report ID', label: 'NCMEC Report ID', placeholder: 'Enter report IDs', validation: 'ncmecReportId' },
    { value: 'Other Payment Instrument', label: 'Other Payment Instrument', placeholder: 'Enter instrument identifiers' },
    { value: 'Other URL', label: 'Other URL', placeholder: 'Enter URLs', validation: 'url', maxLength: 300 },
    {
        value: 'Phone Number',
        label: 'Phone Number',
        placeholder: 'Enter phone numbers',
        example: '+1-555-123-4567',
        validation: 'phone',
    },
    { value: 'PUID', label: 'PUID', placeholder: 'Enter PUIDs', validation: 'puid' },
    { value: 'Serial Number', label: 'Serial Number', placeholder: 'Enter serial numbers' },
    {
        value: 'SharePoint URL',
        label: 'SharePoint URL',
        placeholder: 'Enter SharePoint URLs',
        example: 'https://contoso.sharepoint.com/...',
        validation: 'sharePointUrl',
        maxLength: 2048,
    },
    { value: 'SkypeID', label: 'SkypeID', placeholder: 'Enter Skype IDs', validation: 'skypeId' },
    {
        value: 'Tenant ID',
        label: 'Tenant ID',
        placeholder: 'Enter Microsoft Tenant ID',
        example: '12345678-1234-1234-1234-123456789abc',
        validation: 'guid',
    },
    {
        value: 'Xbox Gamertag',
        label: 'Xbox Gamertag',
        placeholder: 'Enter Xbox Gamertags',
        validation: 'gamertag',
    },
    {
        value: 'XBOX 5X5 Token',
        label: 'XBOX 5X5 Token',
        placeholder: 'Enter 5x5 tokens',
        example: 'XXXXX-XXXXX-XXXXX-XXXXX-XXXXX',
        validation: 'xbox5x5Token',
    },
    { value: 'Other', label: 'Other', placeholder: 'Enter identifier values' },
];

export const identifierTypeOptionMap = IDENTIFIER_TYPES.reduce<Record<string, IdentifierTypeOption>>(
    (acc, option) => {
        acc[option.value] = option;
        return acc;
    },
    {},
);
